import { Link } from 'react-router-dom';
import { Dice5, Eye, ClipboardCheck, Users, ShieldCheck, Terminal } from 'lucide-react';
import { CONTACT } from '@/lib/contact';

// How We Draw Winners — Code clauses 2.1–2.3.
// Dual-mechanism disclosure: cryptographically seeded RNG (mathematical audit)
// PLUS independent witness (procedural audit). Both facts, no exaggeration.
// See T&Cs clause 8 for the legally-binding version.

const Detail = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-pd bg-surface p-6 overflow-hidden">
    <div className="flex items-start gap-4">
      <div className="rounded-xl bg-[color:var(--pd-bg)] p-3 border border-pd shrink-0">
        <Icon size={20} className="text-[color:var(--pd-gold)]" />
      </div>
      {/* min-w-0 is critical inside a flex row — without it the child cannot
          shrink below its content width, which is what caused long inline
          code snippets to spill out of the card on mobile. */}
      <div className="flex-1 min-w-0">
        <h2 className="font-display text-2xl leading-tight break-words">{title}</h2>
        <div className="mt-3 text-sm text-muted-sage leading-relaxed space-y-3 break-words [overflow-wrap:anywhere]">{children}</div>
      </div>
    </div>
  </div>
);

const HowWeDrawWinnersPage = () => (
  <div className="container-pd py-14 md:py-20 max-w-4xl" data-testid="how-we-draw-winners-page">
    <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Draw transparency</div>
    <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">How we draw winners.</h1>
    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--pd-gold)]" data-testid="how-we-draw-motto">Equal Odds, Equal Opportunity.</p>
    <p className="mt-5 text-lg text-muted-sage leading-relaxed max-w-3xl">
      This page is deliberately specific. Two independent checks run in parallel on every draw: one mathematical (a seeded random number generator whose seed and inputs we publish, so anyone can recompute the pick themselves) and one procedural (an independent witness on the video call at the moment the draw happens). We think you deserve to know exactly how both work.
    </p>

    <div className="mt-12 space-y-5">
      <Detail icon={Dice5} title="1. The seeded RNG — the mathematical audit trail.">
        <p>
          At the exact draw time listed on each competition page, our system does four things in strict order: (1) freeze every valid entry for that competition into a sorted list of ticket numbers; (2) generate a 64-bit random seed using the operating system&rsquo;s cryptographically secure random source (Python&rsquo;s <code className="font-mono text-[color:var(--pd-text)]">secrets.randbits</code>); (3) use that seed to deterministically pick one ticket via <code className="font-mono text-[color:var(--pd-text)]">random.Random(seed).choice(sorted(eligible_ticket_numbers))</code>; (4) permanently record the seed, the algorithm, the full eligible ticket list, and the winning ticket to an immutable draw record.
        </p>
        <p>
          That record is what we publish on the <Link to="/past-draws" className="text-[color:var(--pd-accent-yellow)] underline">past-draws log</Link>. Anyone with that seed and eligible list can re-run the maths locally and confirm the same winning ticket falls out. See the &ldquo;Verify it yourself&rdquo; section below for the exact snippet.
        </p>
        <p className="text-xs text-muted-sage">
          Honest caveat: the seed is generated <em>at</em> draw time, not committed in advance. This means the audit trail is fully reproducible after the fact — but it doesn&rsquo;t, on its own, prove we couldn&rsquo;t have re-run the RNG until we got a preferred result. That&rsquo;s exactly why the second check below matters.
        </p>
      </Detail>

      <Detail icon={Eye} title="2. The independent witness — the procedural audit.">
        <p>
          For every draw, a nominated third-party witness — someone with no financial interest in The Padel Draw, and not an employee or contractor — is on the video call while the draw is executed live in the admin dashboard. They see the button pressed, the seed generated, and the winning ticket appear, in one continuous shot. Their name and role are recorded on the draw record at that moment and appear on the public past-draws log alongside the seed and eligible ticket list. The recording of that call is retained for a minimum of six months.
        </p>
        <p>
          If we ever run a draw without a witness present (for example, due to a scheduling conflict), that draw is postponed — not run behind closed doors. There is no scenario in which a draw happens with only us in the room.
        </p>
      </Detail>

      <Detail icon={ShieldCheck} title="Why both, together.">
        <p>
          The seeded RNG lets you verify the maths without needing to trust us or the witness. The witness confirms in real time that we ran the process once, not repeatedly until we liked the answer. Neither mechanism on its own is complete; together they give you both a mathematical proof and a live procedural check on every single draw.
        </p>
      </Detail>

      <Detail icon={Users} title="Free and paid entries have identical odds.">
        <p>
          Every entry sits in one pool. A paid ticket and a free postal entry each get one entry number and one draw slot. The RNG cannot distinguish between them — they are identical at the point of selection. This is a legal requirement under UK prize-competition law, and it is the mechanic that makes the free entry route meaningful.
        </p>
      </Detail>

      <Detail icon={Terminal} title="Verify it yourself — the exact Python snippet.">
        <p>
          For every draw we publish the seed, the algorithm, and the full sorted list of eligible ticket numbers. Paste them into any Python 3 interpreter and you&rsquo;ll get the same winning ticket we did. The five-line script we use is public and looks like this:
        </p>
        <pre className="font-mono text-xs bg-[color:var(--pd-bg)] border border-pd rounded-lg p-4 overflow-x-auto text-[color:var(--pd-text)]">
{`import random

seed = <copied from past-draws log>
eligible = <sorted list of ticket numbers>
winner = random.Random(seed).choice(sorted(eligible))
print(winner)`}
        </pre>
        <p>
          On our <Link to="/past-draws" className="text-[color:var(--pd-accent-yellow)] underline">past-draws page</Link> every completed draw has a &ldquo;Verify&rdquo; link that opens a dedicated view with the exact values pre-filled. If the number you compute doesn&rsquo;t match the published winning ticket, please tell us at <a href={CONTACT.emailHref} className="text-[color:var(--pd-accent-yellow)]">{CONTACT.email}</a>—that would be a fault we&rsquo;d treat as urgent.
        </p>
      </Detail>

      <Detail icon={ClipboardCheck} title="What we publish afterwards.">
        <p>
          Within a working day of every draw, the following goes on this website:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Competition name and slug</li>
          <li>Date and time the draw was run</li>
          <li>Total tickets sold and total valid entries in the pool</li>
          <li>The full seed (64-bit integer)</li>
          <li>The algorithm string</li>
          <li>The sorted list of eligible ticket numbers</li>
          <li>Independent witness name and role</li>
          <li>Winning ticket number</li>
        </ul>
        <p>
          The live log is on our <Link to="/past-draws" className="text-[color:var(--pd-accent-yellow)] underline">past-draws page</Link>. Nothing gets edited or removed from it after publication.
        </p>
      </Detail>
    </div>

    <p className="mt-14 text-xs text-muted-sage">
      If any part of this description is unclear or you want to see how a specific past draw was run, call us on <a href={CONTACT.phoneHref} className="text-[color:var(--pd-accent-yellow)]">{CONTACT.phoneDisplay}</a> or email <a href={CONTACT.emailHref} className="text-[color:var(--pd-accent-yellow)]">{CONTACT.email}</a>.
    </p>
  </div>
);

export default HowWeDrawWinnersPage;
